import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import DeleteIcon from '@material-ui/icons/Delete'
import { FieldArray, Form, Formik, getIn } from 'formik'
import React, { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import BanButton from '../BanButton'
import { DestinationPicker } from '../DestinationPicker'
import { DatePicker } from '../DatePicker'
import * as yup from 'yup'
import {
  regExpEmailCheck,
  phoneForValidation,
} from '../../helpers'
import { Attachments, AttachmentsChangedEvent } from '../Attachments'
import { getAttachmentsFromAttachmentListOrEmpty } from '../../models/attachmentListFnc'
import {
  personNameFilter,
  regExpEmailFilter,
  amountFilter,
  phoneFilter,
  isoDateStrToDateOrNull,
  dateToISO,
} from '../../helpers'
import {
  CasePLRespType,
  CaseCommonDataType,
  TreatmentExpensesRespType,
  PersonRespType,
  CostRespType,
  DamageType,
  ItemRespType,
  CaseReportId,
  AttachmentListRespType,
  AttachmentType,
  DamageTypeEnum,
  PartType,
} from '../../models/caseReport'
import { useStyles } from '../../pages/styled'
import ButtonSimply from '../ButtonSimply'
import TextField from '../TextField'
import { CurrencyPicker } from '../CurrencyPicker'
import ErrorSubmitBack from '../ErrorSubmitBack'
import { Ga4Form } from './Ga4Form'
import { RadioButtonsField } from '../RadioButtonsField'
import { amountReplace, emptyStrToUndefined, objectToLookup, stringToNumber } from '../../helpers/miscFunctions'
import { BooleanField } from '../BooleanField'
import { CheckBoxField } from '../CheckBoxField'

export interface LiabilityInputDataType {
  caseCommon?: CaseCommonDataType | null
  casePL?: CasePLRespType | null
}

export interface LiabilityOutputDataType {
  caseCommon: CaseCommonDataType
  casePL: CasePLRespType
}

export interface LiabilityInputParamsType {
  caseReportId: CaseReportId
  data: LiabilityInputDataType
  isPageConfirmInProgress: boolean
  errorMessage: string
  onPageContinue(value: LiabilityOutputDataType): void
  onPageBack(): void
  notifyDataChanged(isChanged: boolean): void
}

const LiabilityForm = (params: LiabilityInputParamsType) => {
  const classes = useStyles()
  const { t } = useTranslation()

  // BACK
  const handleBackButtonClicked = () => {
    params.onPageBack()
  }

  // SUBMIT
  const handleFormikSubmit = (value: any) => {
    const modelValue: LiabilityOutputDataType =
      transformFormikValuesToOutput(value)
    params.onPageContinue(modelValue)
  }

  function transformInputWitnessesToFormikValues(
    witnesses: PersonRespType[] | null
  ): any[] | null {
    if (witnesses == null) {
      return null
    }

    return witnesses.map((item: any, index) => {
      return {
        orderNum: index + 1,
        firstName: item.firstName ?? '',
        lastName: item.lastName ?? '',
        email: item.email ?? '',
        phone: item.phone ?? '',
        attachmentsTESTIMONY: getAttachmentsFromAttachmentListOrEmpty(
          item.attachmentList,
          AttachmentType.TESTIMONY
        ),
      }
    })
  }

  function transformInputPersonsToFormikValues(
    persons: PersonRespType[] | null
  ): any[] | null {
    if (persons == null) {
      return null
    }

    return persons.map((item: any, index) => {
      return {
        orderNum: index + 1,
        firstName: item.firstName ?? '',
        lastName: item.lastName ?? '',
        email: item.email ?? '',
        phone: item.phone ?? '',
        attachmentsPHOTO: getAttachmentsFromAttachmentListOrEmpty(
          item.attachmentList,
          AttachmentType.PHOTO
        ),
        attachmentsREPORT: getAttachmentsFromAttachmentListOrEmpty(
          item.attachmentList,
          AttachmentType.REPORT
        ),
      }
    })
  }

  function transformInputItemsToFormikValues(
    items: ItemRespType[] | null
  ): any[] | null {
    if (items == null) {
      return null
    }

    return items.map((item, index) => {
      return {
        orderNum: index + 1,
        description: item.description ?? '',
        ownerName: item.ownerName ?? '',
        ownerContact: item.ownerContact ?? '',
        ownerRelative: item.ownerRelative ?? null,
        currency: item.currency ?? '',
        amount: item.amount ?? '',
        attachmentsPHOTO: getAttachmentsFromAttachmentListOrEmpty(
          item.attachmentList,
          AttachmentType.PHOTO
        ),
        attachmentsINVOICE: getAttachmentsFromAttachmentListOrEmpty(
          item.attachmentList,
          AttachmentType.INVOICE
        ),
      }
    })
  }

  function transformCostsToFormikValues(
    costs: CostRespType[] | null
  ): any[] | null {
    if (costs == null) {
      return null
    }

    return costs.map((item) => {
      return {
        amount: item.amount ?? '',
        currency: item.currency ?? '',
        paidDirectly: item.paidDirectly ?? null,
        attachmentsINVOICE: getAttachmentsFromAttachmentListOrEmpty(
          item.attachmentList,
          AttachmentType.INVOICE
        ),
        expenseType: 'LE',
      }
    })
  }

  function transformInputExpensisToFormikValues(
    legalExpenses: TreatmentExpensesRespType | null
  ): any | null {
    if (legalExpenses == null) {
      return null
    }

    return {
      treatmentType: 'LE' ?? '',
      costs: transformCostsToFormikValues(legalExpenses?.costs ?? []),
    }
  }
  function transformFormikItemsToOutput(
    damageType: DamageType,
    itemsFormik: any[]
  ): ItemRespType[] {
    if (itemsFormik == null) {
      return []
    }

    return itemsFormik.map((item: any, index) => {
      const editedAttachmentList: AttachmentListRespType[] = [
        {
          attachmentType: AttachmentType.PHOTO,
          attachments: item.attachmentsPHOTO,
        },
        {
          attachmentType: AttachmentType.INVOICE,
          attachments: item.attachmentsINVOICE,
        },
      ]
      return {
        orderNum: index + 1,
        description: item.description,
        ownerName: item.ownerName,
        ownerContact: item.ownerContact,
        ownerRelative: item.ownerRelative,
        amount: stringToNumber(item.amount),
        currency: emptyStrToUndefined(item.currency),
        attachmentList: editedAttachmentList,
      }
    })
  }

  function transformFormikWitnessesToOutput(
    witnesses: any[]
  ): PersonRespType[] {
    if (witnesses == null) {
      return []
    }

    return witnesses.map((item: any, index) => {
      const editedAttachmentList: AttachmentListRespType[] = [
        {
          attachmentType: AttachmentType.TESTIMONY,
          attachments: item.attachmentsTESTIMONY,
        },
      ]
      return {
        orderNum: index + 1,
        firstName: item.firstName,
        lastName: item.lastName,
        email: item.email,
        phone: item.phone,
        attachmentList: editedAttachmentList,
      }
    })
  }

  function transformFormikPersonsToOutput(persons: any[]): PersonRespType[] {
    if (persons == null) {
      return []
    }

    return persons.map((item: any, index) => {
      const editedAttachmentList: AttachmentListRespType[] = [
        {
          attachmentType: AttachmentType.PHOTO,
          attachments: item.attachmentsPHOTO,
        },
        {
          attachmentType: AttachmentType.REPORT,
          attachments: item.attachmentsREPORT,
        },
      ]
      return {
        orderNum: index + 1,
        firstName: item.firstName,
        lastName: item.lastName,
        email: item.email,
        phone: item.phone,
        attachmentList: editedAttachmentList,
      }
    })
  }

  function transformCostsToOutput(costs: CostRespType[] | null): CostRespType[] | null {
    if (costs == null) {
      return null
    }

    return costs.map((item: any, index) => {
      const editedAttachmentList: AttachmentListRespType[] = [
        {
          attachmentType: AttachmentType.INVOICE,
          attachments: item.attachmentsINVOICE,
        },
      ]
      return {
        orderNum: index + 1,
        amount: stringToNumber(item.amount),
        currency: emptyStrToUndefined(item.currency),
        paidDirectly: true, // by ERGO request 
        expenseType: 'LE',
        attachmentList: editedAttachmentList,
      }
    })
  }

  function transformExpensisToOutput(
    legalExpensis: TreatmentExpensesRespType
  ): TreatmentExpensesRespType {
    return {
      treatmentType: 'LE',
      costs: transformCostsToOutput(legalExpensis.costs),
    }
  }

  function transformInputToFormikValues(input: LiabilityInputDataType): any {
    return {
      occurredOn: isoDateStrToDateOrNull(input.caseCommon?.occurredOn),
      destination: input.caseCommon?.destination ?? null,
      resort: input.caseCommon?.resort ?? '',
      place: input.caseCommon?.place ?? '',
      damageType: input.casePL?.damageType ?? null,
      description: input.caseCommon?.description ?? '',
      assistance: input.caseCommon?.assistance ?? null,
      assistanceDesc: input.caseCommon?.assistanceDesc ?? '',
      witnessExists: input.casePL?.witnessExists ?? false,
      persons: transformInputPersonsToFormikValues(
        input.casePL?.persons ?? null
      ),
      witnesses: transformInputWitnessesToFormikValues(
        input.casePL?.witnesses ?? null
      ),
      items: transformInputItemsToFormikValues(input.casePL?.items ?? null),
      legalExpensesExists: input.casePL?.legalExpensesExists ?? null,
      legalExpenses: transformInputExpensisToFormikValues(
        input.casePL?.legalExpenses ?? null
      ),
    }
  }

  function transformFormikValuesToOutput(values: any): LiabilityOutputDataType {

    const caseCommonFromFormik: CaseCommonDataType = {
      occurredOn: dateToISO(values.occurredOn),
      destination: values.destination,
      resort: values.resort,
      place: values.place,
      description: values.description,
      assistance: values.assistance,
      assistanceDesc: values.assistance === false ? values.assistanceDesc : undefined,
    }
    const damageType: DamageType = values.damageType
    const casePLFromFormik: CasePLRespType = {
      damageType: damageType,
      witnessExists: values.witnessExists,
      witnesses:
        values.witnessExists
          ? transformFormikWitnessesToOutput(values.witnesses)
          : undefined,
      items:
        damageType === DamageTypeEnum.PROPERTY
          ? transformFormikItemsToOutput(damageType, values.items)
          : undefined,
      persons:
        damageType === DamageTypeEnum.BODY
          ? transformFormikPersonsToOutput(values.persons)
          : undefined,
      legalExpensesExists: values.legalExpensesExists,
      legalExpenses: values.legalExpensesExists
        ? transformExpensisToOutput(values.legalExpenses)
        : undefined,
    }
    return {
      caseCommon: caseCommonFromFormik,
      casePL: casePLFromFormik,
    }
  }

  const LiablityValidationSchema = () =>
    yup.object().shape({
      occurredOn: yup
        .date()
        .nullable()
        .required(t('liabilityForm.occurredOn.required'))
        .min(new Date('1999-12-31'), t('dateMessage.err2'))
        .max(new Date(), t('dateMessage.err2'))
        .typeError(t('dateMessage.err1')),
      destination: yup
        .string()
        .required(t('liabilityForm.destination.required'))
        .nullable(),
      place: yup.string().required(t('liabilityForm.place.required')),
      resort: yup
        .string()
        .required(t('liabilityForm.resort.required'))
        .test('len', t('liabilityForm.resort.length'), (val: any) => {
          return val?.length <= 50
        }),
      description: yup.string(),
      damageType: yup.string().nullable().required(t('liabilityForm.damageType.required')),
      assistance: yup.boolean().nullable().required(t('optionRequired')),
      assistanceDesc: yup
        .mixed()
        .nullable()
        .when('assistance', {
          is: (assistance: any) => assistance === false,
          then: yup
            .string()
            .required(t('liabilityForm.assistanceDesc.required'))
            .test(
              'len',
              t('liabilityForm.assistanceDesc.length'),
              (val: any) => {
                return val?.length <= 254
              }
            ),
        }),
      witnessExists: yup.boolean().nullable(),
      witnesses: yup.array().when('witnessExists', {
        is: (witnessExists: any) => witnessExists === true,
        then: yup.array().of(
          yup.object().shape({
            firstName: yup
              .string()
              .required(t('liabilityForm.witnesses.firstName.required'))
              .min(2, t('lengthText')),
            lastName: yup
              .string()
              .required(t('liabilityForm.witnesses.lastName.required'))
              .min(2, t('lengthText')),
            email: yup
              .string()
              .matches(regExpEmailCheck, t('email.formatErr'))
              .required(t('email.required')),
            phone: yup
              .string()
              .matches(
                phoneForValidation,
                t('liabilityForm.witnesses.phone.err1')
              )
              .required(t('liabilityForm.witnesses.phone.required')),
            attachmentsTESTIMONY: yup
              .array()
              .nullable()
              .min(1, t('uploadFile.err1')),
          })
        ),
      }),
      persons: yup.array().when('damageType', {
        is: (damageType: any) => damageType === DamageTypeEnum.BODY,
        then: yup.array().of(
          yup.object().shape({
            firstName: yup
              .string()
              .required(t('liabilityForm.persons.firstName.required'))
              .min(2, t('lengthText')),
            lastName: yup
              .string()
              .required(t('liabilityForm.persons.lastName.required'))
              .min(2, t('lengthText')),
            email: yup
              .string()
              .matches(regExpEmailCheck, t('email.formatErr'))
              .required(t('email.required')),
            phone: yup
              .string()
              .matches(
                phoneForValidation,
                t('liabilityForm.persons.phone.err1')
              )
              .required(t('liabilityForm.persons.phone.required')),
            attachmentsREPORT: yup
              .array()
              .nullable()
              .min(1, t('uploadFile.err1')),
            attachmentsPHOTO: yup
              .array()
              .nullable()
              .min(1, t('uploadFile.err1')),
          })
        ),
      }),
      items: yup.array().when('damageType', {
        is: (damageType: any) => damageType === DamageTypeEnum.PROPERTY,
        then: yup.array().of(
          yup.object().shape({
            description: yup
              .string()
              .required(t('liabilityForm.items.description.required'))
              .min(2, t('lengthText')),
            ownerName: yup
              .string()
              .required(t('liabilityForm.items.ownerName.required'))
              .min(2, t('lengthText')),
            ownerContact: yup
              .string()
              .required(t('liabilityForm.items.ownerContact.required'))
              .min(2, t('lengthText')),
            ownerRelative: yup
              .boolean()
              .nullable()
              .required(t('optionRequired')),
            amount: yup
              .string()
              .required(t('liabilityForm.items.amount.required'))
              .test(
                'numberFormat',
                t('message.err'),
                (val: string | undefined) => {
                  const _number = Number(val)
                  return !isNaN(_number)
                }
              )
              .test(
                'moreThan0',
                t('amount.moreThan0'),
                (val: string | undefined) => {
                  const _number = Number(val)
                  return _number > 0
                }
              ),
            currency: yup.string().nullable().required(t('currency.required')),
            attachmentsINVOICE: yup.array().nullable(),
            attachmentsPHOTO: yup
              .array()
              .nullable()
              .min(1, t('uploadFile.err1')),
          })
        ),
      }),
      legalExpensesExists: yup
        .boolean()
        .nullable()
        .required(t('optionRequired')),
      legalExpenses: yup
        .object()
        .nullable()
        .when('legalExpensesExists', {
          is: (legalExpensesExists: any) => legalExpensesExists === true,
          then: LegalExpensesSchema,
        }),
    })

  const LegalExpensesSchema = () =>
    yup
      .object()
      .nullable()
      .shape({
        costs: yup
          .array()
          .nullable()
          .of(
            yup.object().shape({
              amount: yup
                .string()
                .required(t('liabilityForm.items.amount.required'))
                .test(
                  'numberFormat',
                  t('message.err'),
                  (val: string | undefined) => {
                    const _number = Number(val)
                    return !isNaN(_number)
                  }
                )
                .test(
                  'moreThan0',
                  t('liabilityForm.items.amount.moreThan0'),
                  (val: string | undefined) => {
                    const _number = Number(val)
                    return _number > 0
                  }
                ),
              currency: yup
                .string()
                .nullable()
                .required(t('currency.required')),
              /* suppressed by ERGO requets
              paidDirectly: yup
                .string()
                .nullable()
                .required(t('optionRequired')),
              attachmentsINVOICE: yup.mixed().when('paidDirectly', {
                is: (paidDirectly: any) => paidDirectly === 'true',
                then: yup.array().nullable().min(1, t('uploadFile.err1')),
              }),
              */
              attachmentsINVOICE: yup
                .array()
                .nullable()
                .min(1, t('uploadFile.err1')),
            })
          ),
      })

  const formikInitialValues = transformInputToFormikValues(params?.data)

  const formikValueForNewItem = {
    description: '',
    ownerName: '',
    ownerContact: '',
    ownerRelative: null,
    currency: null,
    amount: '',
    attachmentsPHOTO: [],
    attachmentsINVOICE: [],
  }

  if (
    formikInitialValues.items == null ||
    formikInitialValues.items.length === 0
  ) {
    formikInitialValues.items = [formikValueForNewItem]
  }

  const formikValueForNewWitness = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    attachmentsTESTIMONY: [],
  }

  if (
    formikInitialValues.witnesses == null ||
    formikInitialValues.witnesses.length === 0
  ) {
    formikInitialValues.witnesses = [formikValueForNewWitness]
  }

  const formikValueForNewPersons = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    attachmentsPHOTO: [],
    attachmentsREPORT: [],
  }

  if (
    formikInitialValues.persons == null ||
    formikInitialValues.persons.length === 0
  ) {
    formikInitialValues.persons = [formikValueForNewPersons]
  }

  const formikValueForNewLegalExpenseCosts = {
    treatmentType: 'LE',
    costs: [
      {
        orderNum: 0,
        amount: '',
        currency: null,
        paidDirectly: null,
        expenseType: 'LE',
        attachmentsINVOICE: [],
      },
    ],
  }

  if (
    formikInitialValues.legalExpenses?.costs == null ||
    formikInitialValues.legalExpenses?.costs.length === 0
  ) {
    formikInitialValues.legalExpenses = {
      costs: [formikValueForNewLegalExpenseCosts.costs[0]],
    }
  }

  const damageTypeLookup = useMemo(() =>
    objectToLookup(t('damageTypes', { returnObjects: true })).filter((item) =>
      [DamageTypeEnum.PROPERTY, DamageTypeEnum.BODY].includes(item.code as DamageTypeEnum)
    )
    , [t])
  const formikRef = useRef(null)
  return (
    <Ga4Form formName={'LiabilityForm'} >
      <Formik
        initialValues={formikInitialValues}
        onSubmit={(values) => handleFormikSubmit(values)}
        validationSchema={LiablityValidationSchema}
        /* Enables re-setting of formik initialValues from props */
        enableReinitialize={true}
        innerRef={formikRef}
      >
        {({
          initialValues,
          values,
          errors,
          touched,
          setFieldValue,
          handleChange,
          handleBlur,
        }) => {
          /**
           * Local truncation of repeating code
           */
          function isError(name: string): boolean {
            return Boolean(
              getIn(errors, name) &&
              getIn(touched, name))
          }
          return (
            <Form className={classes.root}>
              <Grid
                item
                sm={12}
                xl={12}
                xs={12}
                container
                direction="row"
                className={classes.gridMainForm}
              >
                <Grid
                  item
                  sm={9}
                  xl={9}
                  xs={12}
                  container
                  direction="row"
                  className={classes.gridWorkArea}
                >
                  <Grid
                    item
                    sm={12}
                    xs={12}
                    xl={12}
                    container
                    className={classes.gridResize}
                  >
                    <Typography className={classes.text} variant="h3">
                      {t('liabilityForm.title')}
                    </Typography>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12}>
                        <DatePicker
                          label={t('liabilityForm.occurredOn.label')}
                          name="occurredOn"
                          error={isError('occurredOn')}
                          helperText={isError('occurredOn')
                            ? errors.occurredOn
                            : null
                          }
                          required={true}
                          value={values.occurredOn}
                          onChange={(value) => {
                            setFieldValue('occurredOn', value)
                          }}
                          minDate={new Date('2000, 0, 1')}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.fieldSize}
                      >
                        <DestinationPicker
                          name={'destination'}
                          selectedItemCode={values.destination}
                          onValueChanged={(
                            selectedDestination: string | null
                          ) => {
                            setFieldValue('destination', selectedDestination)
                          }}
                          label={t('liabilityForm.destination.label')}
                          error={isError('destination')}
                          helperText={isError('destination')
                            ? errors.destination
                            : null
                          }
                          required={true}
                          onBlur={handleBlur}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item sm={6} xl={6} xs={6}>
                        <TextField
                          name="resort"
                          label={t('liabilityForm.resort.label')}
                          placeholder={t('liabilityForm.resort.hint')}
                          value={values.resort}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={isError('resort')}
                          helperText={isError('resort') ? errors.resort : null}
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                          required
                        />
                      </Grid>
                      <Grid item sm={6} xl={6} xs={6}>
                        <TextField
                          name="place"
                          label={t('liabilityForm.place.label')}
                          placeholder={t('liabilityForm.place.hint')}
                          value={values.place}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={isError('place')}
                          helperText={isError('place') ? errors.place : null}
                          className={classes.fieldSize}
                          inputProps={{
                            maxLength: 50,
                          }}
                          InputProps={{ required: false }}
                          required
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                        <RadioButtonsField
                          aria-label={'damageType'}
                          name={'damageType'}
                          label={t('liabilityForm.damageType.label')}
                          required
                          value={values.damageType}
                          onChange={handleChange}
                          lookup={damageTypeLookup}
                          error={isError('damageType')}
                          helperText={isError('damageType')
                            ? errors.damageType
                            : null
                          }
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        sm={12}
                        xl={12}
                        xs={12}
                        className={classes.gridResize}
                      >
                        <TextField
                          label={t('liabilityForm.description.label')}
                          aria-label="description"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          multiline
                          rows={7}
                          rowsMax={7}
                          placeholder={t('liabilityForm.description.hint')}
                          className={classes.textAreaClass}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                        <BooleanField
                          aria-label="assistance"
                          name="assistance"
                          value={values.assistance}
                          label={t('liabilityForm.assistance.label')}
                          onValueChange={(value) => setFieldValue('assistance', value)}
                          required
                          yesText={t('yes')}
                          noText={t('no')}
                          noFirst
                          error={isError('assistance')}
                          helperText={isError('assistance') ? errors.assistance : null}
                        />
                      </Grid>
                    </Grid>
                    {values.assistance === false && (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        xl={12}
                        container
                        className={classes.gridItem}
                        spacing={2}
                      >
                        <Grid
                          item
                          sm={12}
                          xl={12}
                          xs={12}
                          className={classes.fieldSize}
                        >
                          <TextField
                            name="assistanceDesc"
                            value={values.assistanceDesc}
                            label={t('liabilityForm.assistanceDesc.label')}
                            placeholder={t('liabilityForm.assistanceDesc.hint')}
                            onChange={handleChange}
                            error={isError('assistanceDesc')}
                            helperText={isError('assistanceDesc')
                              ? errors.assistanceDesc
                              : null
                            }
                            className={classes.fieldSize}
                            InputProps={{ required: false }}
                            onBlur={handleBlur}
                            required
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xs={12} sm={12} xl={12}>
                        <CheckBoxField
                          name="witnessExists"
                          value={values.witnessExists}
                          label={t('liabilityForm.witnessExists')}
                          checked={values.witnessExists}
                          onChange={
                            (e, checked) => {
                              setFieldValue('witnessExists', checked)
                            }
                          }
                        />
                      </Grid>
                    </Grid>
                    {values.witnessExists && (
                      <FieldArray
                        name="witnesses"
                        render={({ push, remove }) => (
                          <React.Fragment>
                            {values?.witnesses &&
                              Boolean(values?.witnesses.length) &&
                              values?.witnesses.map(
                                (witness: any, index: any) => {
                                  return (
                                    <React.Fragment key={index}>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        {index !== 0 && (
                                          <Grid
                                            item
                                            xl={12}
                                            xs={12}
                                            sm={12}
                                            className={classes.rowAlign}
                                          >
                                            <Typography
                                              className={classes.text}
                                              variant="h3"
                                            >
                                              {index > 0
                                                ? t(
                                                  `liabilityForm.witnesses.titleN`,
                                                  { N: (index as number) + 1 }
                                                )
                                                : ''}
                                            </Typography>
                                            {index > 0 ? (
                                              index ===
                                              (values?.witnesses &&
                                                values?.witnesses.length -
                                                1) && (
                                                <BanButton
                                                  onClick={() => remove(index)}
                                                >
                                                  <DeleteIcon />
                                                </BanButton>
                                              )
                                            ) : (
                                              <></>
                                            )}
                                          </Grid>
                                        )}
                                        <Grid
                                          item
                                          xl={6}
                                          xs={6}
                                          sm={6}
                                          className={classes.fieldSize}
                                        >
                                          <TextField
                                            name={`witnesses[${index}].firstName`}
                                            value={
                                              values.witnesses[index]?.firstName
                                            }
                                            label={t(
                                              'liabilityForm.witnesses.firstName.label'
                                            )}
                                            placeholder={t(
                                              'liabilityForm.witnesses.firstName.hint'
                                            )}
                                            required
                                            regex={personNameFilter}
                                            inputProps={{
                                              maxLength: 59,
                                            }}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={isError(`witnesses[${index}].firstName`)}
                                            helperText={isError(`witnesses[${index}].firstName`)
                                              ? getIn(
                                                errors,
                                                `witnesses[${index}].firstName`
                                              )
                                              : null
                                            }
                                            className={classes.fieldSize}
                                            InputProps={{ required: false }}
                                          />
                                        </Grid>
                                        <Grid
                                          item
                                          xl={6}
                                          xs={6}
                                          sm={6}
                                          className={classes.fieldSize}
                                        >
                                          <TextField
                                            name={`witnesses[${index}].lastName`}
                                            value={
                                              values.witnesses[index]?.lastName
                                            }
                                            label={t(
                                              'liabilityForm.witnesses.lastName.label'
                                            )}
                                            placeholder={t(
                                              'liabilityForm.witnesses.lastName.hint'
                                            )}
                                            required
                                            regex={personNameFilter}
                                            inputProps={{
                                              maxLength: 60,
                                            }}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            error={isError(`witnesses[${index}].lastName`)}
                                            helperText={isError(`witnesses[${index}].lastName`)
                                              ? getIn(
                                                errors,
                                                `witnesses[${index}].lastName`
                                              )
                                              : null
                                            }
                                            className={classes.fieldSize}
                                            InputProps={{ required: false }}
                                          />
                                        </Grid>
                                      </Grid>
                                      <Grid
                                        item
                                        sm={12}
                                        xl={12}
                                        xs={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Grid
                                          item
                                          sm={6}
                                          xl={6}
                                          xs={6}
                                          className={classes.fieldSize}
                                        >
                                          <TextField
                                            name={`witnesses[${index}].email`}
                                            label={t(
                                              'liabilityForm.witnesses.email.label'
                                            )}
                                            placeholder={t(
                                              'liabilityForm.witnesses.email.hint'
                                            )}
                                            value={values.witnesses[index]?.email}
                                            required
                                            regex={regExpEmailFilter}
                                            inputProps={{ maxLength: 128 }}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={isError(`witnesses[${index}].email`)}
                                            helperText={isError(`witnesses[${index}].email`)
                                              ? getIn(
                                                errors,
                                                `witnesses[${index}].email`
                                              )
                                              : null
                                            }
                                            autoComplete="email"
                                            InputProps={{ required: false }}
                                            className={classes.fieldSize}
                                          />
                                        </Grid>
                                        <Grid
                                          item
                                          sm={6}
                                          xl={6}
                                          xs={6}
                                          className={classes.fieldSize}
                                        >
                                          <TextField
                                            name={`witnesses[${index}].phone`}
                                            value={values.witnesses[index]?.phone}
                                            label={t(
                                              'liabilityForm.witnesses.phone.label'
                                            )}
                                            placeholder={t(
                                              'liabilityForm.witnesses.phone.hint'
                                            )}
                                            required
                                            regex={phoneFilter}
                                            inputProps={{ maxLength: 15 }}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={isError(`witnesses[${index}].phone`)}
                                            helperText={isError(`witnesses[${index}].phone`)
                                              ? getIn(
                                                errors,
                                                `witnesses[${index}].phone`
                                              )
                                              : null
                                            }
                                            InputProps={{ required: false }}
                                            className={classes.fieldSize}
                                          />
                                        </Grid>
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Attachments
                                          name={`witnesses[${index}].attachmentsTESTIMONY`}
                                          caseReportId={params.caseReportId}
                                          labelText={t(
                                            'uploadAttachments.WITNESS.TESTIMONY'
                                          )}
                                          attachmentType={
                                            AttachmentType.TESTIMONY
                                          }
                                          part={PartType.WITNESS}
                                          itemOrderNum={index + 1}
                                          attachments={
                                            values.witnesses[index]
                                              ?.attachmentsTESTIMONY
                                          }
                                          onAttachmentsChanged={(
                                            event: AttachmentsChangedEvent
                                          ) => {
                                            setFieldValue(
                                              `witnesses[${index}].attachmentsTESTIMONY`,
                                              event.value.attachments
                                            )
                                          }}
                                          onBlur={handleBlur}
                                          required
                                          error={isError(`witnesses[${index}].attachmentsTESTIMONY`)}
                                          helperText={isError(`witnesses[${index}].attachmentsTESTIMONY`)
                                            ? getIn(
                                              errors,
                                              `witnesses[${index}].attachmentsTESTIMONY`
                                            )
                                            : null
                                          }
                                        />
                                      </Grid>
                                      <Grid
                                        item
                                        xl={12}
                                        xs={12}
                                        sm={12}
                                        className={classes.row}
                                      >
                                        <Grid
                                          item
                                          xl={6}
                                          xs={6}
                                          sm={6}
                                          className={classes.fieldSize}
                                        >
                                          <ButtonSimply
                                            onClick={() => {
                                              const initialValue = {
                                                ...formikValueForNewWitness,
                                              }
                                              push(initialValue)
                                            }}
                                          >
                                            {t(
                                              'liabilityForm.witnesses.addPerson'
                                            )}
                                          </ButtonSimply>
                                        </Grid>
                                      </Grid>
                                    </React.Fragment>
                                  )
                                }
                              )}
                          </React.Fragment>
                        )}
                      />
                    )}
                    {values.damageType === DamageTypeEnum.BODY && (
                      <React.Fragment>
                        <FieldArray
                          name="persons"
                          render={({ push, remove }) => (
                            <React.Fragment>
                              {values?.persons &&
                                Boolean(values?.persons.length) &&
                                values?.persons.map((person: any, index: any) => {
                                  return (
                                    <React.Fragment key={index}>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Grid
                                          item
                                          xs={12}
                                          sm={12}
                                          xl={12}
                                          className={classes.rowAlign}
                                        >
                                          <Typography
                                            className={classes.text}
                                            variant="h3"
                                          >
                                            {index === 0
                                              ? t('liabilityForm.persons.title0')
                                              : t(
                                                `liabilityForm.persons.titleN`,
                                                { N: (index as number) + 1 }
                                              )}
                                          </Typography>
                                          {index > 0 ? (
                                            values?.persons &&
                                            values?.persons.length - 1 && (
                                              <BanButton
                                                onClick={() => remove(index)}
                                              >
                                                <DeleteIcon />
                                              </BanButton>
                                            )
                                          ) : (
                                            <></>
                                          )}
                                        </Grid>
                                        <Grid
                                          item
                                          xl={6}
                                          xs={6}
                                          sm={6}
                                          className={classes.fieldSize}
                                        >
                                          <TextField
                                            name={`persons[${index}].firstName`}
                                            value={
                                              values.persons[index]?.firstName
                                            }
                                            label={t(
                                              'liabilityForm.persons.firstName.label'
                                            )}
                                            placeholder={t(
                                              'liabilityForm.persons.firstName.hint'
                                            )}
                                            required
                                            regex={personNameFilter}
                                            inputProps={{
                                              maxLength: 59,
                                            }}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={isError(`persons[${index}].firstName`)}
                                            helperText={isError(`persons[${index}].firstName`)
                                              ? getIn(
                                                errors,
                                                `persons[${index}].firstName`
                                              )
                                              : null
                                            }
                                            className={classes.fieldSize}
                                            InputProps={{ required: false }}
                                          />
                                        </Grid>
                                        <Grid
                                          item
                                          xl={6}
                                          xs={6}
                                          sm={6}
                                          className={classes.fieldSize}
                                        >
                                          <TextField
                                            name={`persons[${index}].lastName`}
                                            value={
                                              values.persons[index]?.lastName
                                            }
                                            label={t(
                                              'liabilityForm.persons.lastName.label'
                                            )}
                                            placeholder={t(
                                              'liabilityForm.persons.lastName.hint'
                                            )}
                                            required
                                            regex={personNameFilter}
                                            inputProps={{
                                              maxLength: 60,
                                            }}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={isError(`persons[${index}].lastName`)}
                                            helperText={isError(`persons[${index}].lastName`)
                                              ? getIn(
                                                errors,
                                                `persons[${index}].lastName`
                                              )
                                              : null
                                            }
                                            className={classes.fieldSize}
                                            InputProps={{ required: false }}
                                          />
                                        </Grid>
                                      </Grid>
                                      <Grid
                                        item
                                        sm={12}
                                        xl={12}
                                        xs={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Grid
                                          item
                                          sm={6}
                                          xl={6}
                                          xs={6}
                                          className={classes.fieldSize}
                                        >
                                          <TextField
                                            name={`persons[${index}].email`}
                                            value={values.persons[index]?.email}
                                            label={t(
                                              'liabilityForm.persons.email.label'
                                            )}
                                            placeholder={t(
                                              'liabilityForm.persons.email.hint'
                                            )}
                                            required
                                            regex={regExpEmailFilter}
                                            inputProps={{ maxLength: 128 }}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={isError(`persons[${index}].email`)}
                                            helperText={isError(`persons[${index}].email`)
                                              ? getIn(
                                                errors,
                                                `persons[${index}].email`
                                              )
                                              : null
                                            }
                                            InputProps={{ required: false }}
                                            className={classes.fieldSize}
                                          />
                                        </Grid>
                                        <Grid
                                          item
                                          sm={6}
                                          xl={6}
                                          xs={6}
                                          className={classes.fieldSize}
                                        >
                                          <TextField
                                            name={`persons[${index}].phone`}
                                            value={values.persons[index]?.phone}
                                            label={t(
                                              'liabilityForm.persons.phone.label'
                                            )}
                                            placeholder={t(
                                              'liabilityForm.persons.phone.hint'
                                            )}
                                            required
                                            regex={phoneFilter}
                                            inputProps={{ maxLength: 15 }}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={isError(`persons[${index}].phone`)}
                                            helperText={isError(`persons[${index}].phone`)
                                              ? getIn(
                                                errors,
                                                `persons[${index}].phone`
                                              )
                                              : null
                                            }
                                            InputProps={{ required: false }}
                                            className={classes.fieldSize}
                                          />
                                        </Grid>
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Attachments
                                          name={`persons[${index}].attachmentsPHOTO`}
                                          caseReportId={params.caseReportId}
                                          labelText={t(
                                            'uploadAttachments.PERSON.PHOTO'
                                          )}
                                          attachmentType={AttachmentType.PHOTO}
                                          part={PartType.PERSON}
                                          //itemOrderNum=undefined
                                          attachments={
                                            values.persons[index]
                                              ?.attachmentsPHOTO
                                          }
                                          onAttachmentsChanged={(
                                            event: AttachmentsChangedEvent
                                          ) => {
                                            setFieldValue(
                                              `persons[${index}].attachmentsPHOTO`,
                                              event.value.attachments
                                            )
                                          }}
                                          onBlur={handleBlur}
                                          required={true}
                                          error={isError(`persons[${index}].attachmentsPHOTO`)}
                                          helperText={isError(`persons[${index}].attachmentsPHOTO`)
                                            ? getIn(
                                              errors,
                                              `persons[${index}].attachmentsPHOTO`
                                            )
                                            : null
                                          }
                                        />
                                      </Grid>
                                      <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        xl={12}
                                        container
                                        className={classes.gridItem}
                                        spacing={2}
                                      >
                                        <Attachments
                                          name={`persons[${index}].attachmentsREPORT`}
                                          caseReportId={params.caseReportId}
                                          labelText={t(
                                            'uploadAttachments.PERSON.REPORT'
                                          )}
                                          attachmentType={AttachmentType.REPORT}
                                          part={PartType.PERSON}
                                          //itemOrderNum=undefined
                                          attachments={
                                            values.persons[index]
                                              ?.attachmentsREPORT
                                          }
                                          onAttachmentsChanged={(
                                            event: AttachmentsChangedEvent
                                          ) => {
                                            /*console.log(
                                              'onAttachmentsChanged: ',
                                              event
                                            )*/
                                            // aktualizace values formiku
                                            setFieldValue(
                                              `persons[${index}].attachmentsREPORT`,
                                              event.value.attachments
                                            )
                                          }}
                                          onBlur={handleBlur}
                                          required={true}
                                          error={isError(`persons[${index}].attachmentsREPORT`)}
                                          helperText={isError(`persons[${index}].attachmentsREPORT`)
                                            ? getIn(
                                              errors,
                                              `persons[${index}].attachmentsREPORT`
                                            )
                                            : null
                                          }
                                        />
                                      </Grid>
                                      {/* ************Suppressed by ERGO request
                                    <Grid
                                      item
                                      xl={12}
                                      xs={12}
                                      sm={12}
                                      className={classes.row}
                                    >
                                      <Grid
                                        item
                                        xl={6}
                                        xs={6}
                                        sm={6}
                                        className={classes.fieldSize}
                                      >
                                        <ButtonSimply
                                          onClick={() => {
                                            const initialValue = {
                                              ...formikValueForNewPersons,
                                            }
                                            push(initialValue)
                                          }}
                                        >
                                          {t('thirdPg.addPerson')}
                                        </ButtonSimply>
                                      </Grid>
                                    </Grid>
                                    ********************* */}
                                    </React.Fragment>
                                  )
                                })}
                            </React.Fragment>
                          )}
                        />
                      </React.Fragment>
                    )}
                    {values.damageType === DamageTypeEnum.PROPERTY && (
                      <FieldArray
                        name="items"
                        render={({ push, remove }) => (
                          <React.Fragment>
                            {values?.items != null &&
                              values.items.map((item: any, index: any) => {
                                return (
                                  <React.Fragment key={index}>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={12}
                                      xl={12}
                                      container
                                      className={classes.gridItem}
                                      spacing={2}
                                    >
                                      <Grid
                                        item
                                        xl={12}
                                        xs={12}
                                        sm={12}
                                        className={classes.rowAlign}
                                      >
                                        <Typography
                                          className={classes.text}
                                          variant="h3"
                                        >
                                          {index === 0
                                            ? t('liabilityForm.items.title0')
                                            : t(`liabilityForm.items.titleN`, {
                                              N: (index as number) + 1,
                                            })}
                                        </Typography>
                                        {index > 0 ? (
                                          values?.items &&
                                          values?.items.length - 1 && (
                                            <BanButton
                                              onClick={() => remove(index)}
                                            >
                                              <DeleteIcon />
                                            </BanButton>
                                          )
                                        ) : (
                                          <></>
                                        )}
                                      </Grid>
                                      <Grid
                                        item
                                        xl={12}
                                        xs={12}
                                        sm={12}
                                        className={classes.fieldSize}
                                      >
                                        <TextField
                                          name={`items[${index}].description`}
                                          value={
                                            values?.items[index]?.description
                                          }
                                          label={t(
                                            'liabilityForm.items.description.label'
                                          )}
                                          placeholder={t(
                                            'liabilityForm.items.description.hint'
                                          )}
                                          required
                                          inputProps={{
                                            maxLength: 50,            // fixed 23.5.2023 by ZHE
                                          }}
                                          onChange={(e) => {
                                            setFieldValue(
                                              `items[${index}].description`,
                                              e.target.value
                                            )
                                          }}
                                          onBlur={handleBlur}
                                          error={isError(`items[${index}].description`)}
                                          helperText={isError(`items[${index}].description`)
                                            ? getIn(
                                              errors,
                                              `items[${index}].description`
                                            )
                                            : null
                                          }
                                          className={classes.fieldSize}
                                          InputProps={{ required: false }}
                                        />
                                      </Grid>
                                      <Grid
                                        item
                                        xl={12}
                                        xs={12}
                                        sm={12}
                                        className={classes.fieldSize}
                                      >
                                        <TextField
                                          name={`items[${index}].ownerName`}
                                          value={values?.items[index]?.ownerName}
                                          label={t(
                                            'liabilityForm.items.ownerName.label'
                                          )}
                                          placeholder={t(
                                            'liabilityForm.items.ownerName.hint'
                                          )}
                                          required
                                          inputProps={{ maxLength: 50 }}     // fixed 23.5.2023 by ZHE
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={isError(`items[${index}].ownerName`)}
                                          helperText={isError(`items[${index}].ownerName`)
                                            ? getIn(
                                              errors,
                                              `items[${index}].ownerName`
                                            )
                                            : null
                                          }
                                          className={classes.fieldSize}
                                          InputProps={{ required: false }}
                                        />
                                      </Grid>
                                      <Grid
                                        item
                                        xl={12}
                                        xs={12}
                                        sm={12}
                                        className={classes.fieldSize}
                                      >
                                        <TextField
                                          name={`items[${index}].ownerContact`}
                                          value={
                                            values?.items[index]?.ownerContact
                                          }
                                          label={t(
                                            'liabilityForm.items.ownerContact.label'
                                          )}
                                          placeholder={t(
                                            'liabilityForm.items.ownerContact.hint'
                                          )}
                                          required
                                          inputProps={{ maxLength: 120 }}
                                          onChange={handleChange}
                                          onBlur={handleBlur}
                                          error={isError(`items[${index}].ownerContact`)}
                                          helperText={isError(`items[${index}].ownerContact`)
                                            ? getIn(
                                              errors,
                                              `items[${index}].ownerContact`
                                            )
                                            : null
                                          }
                                          className={classes.fieldSize}
                                          InputProps={{ required: false }}
                                        />
                                      </Grid>
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={12}
                                      xl={12}
                                      container
                                      className={classes.gridItem}
                                      spacing={2}
                                    >
                                      <Grid
                                        item
                                        xl={12}
                                        xs={12}
                                        sm={12}
                                        className={classes.row}
                                      >
                                        <BooleanField
                                          name={`items[${index}].ownerRelative`}
                                          label={t('liabilityForm.items.ownerRelative.label')}
                                          required
                                          value={values.items[index].ownerRelative}
                                          onValueChange={(value) => setFieldValue(`items[${index}].ownerRelative`, value)}
                                          yesText={t('yes')}
                                          noText={t('no')}
                                          noFirst
                                          error={isError(`items[${index}].ownerRelative`)}
                                          helperText={isError(`items[${index}].ownerRelative`)
                                            ? getIn(
                                              errors,
                                              `items[${index}].ownerRelative`
                                            )
                                            : null
                                          }
                                        />
                                      </Grid>
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={12}
                                      xl={12}
                                      container
                                      className={classes.gridItem}
                                      spacing={2}
                                    >
                                      <Grid
                                        item
                                        xl={6}
                                        xs={6}
                                        sm={6}
                                        className={classes.fieldSize}
                                      >
                                        <TextField
                                          name={`items[${index}].amount`}
                                          value={values?.items[index]?.amount}
                                          label={t(
                                            'liabilityForm.items.amount.label'
                                          )}
                                          placeholder={t(
                                            'liabilityForm.items.amount.hint'
                                          )}
                                          required
                                          regex={amountFilter}
                                          inputProps={{ maxLength: 12 }}
                                          onChange={(e) => {
                                            setFieldValue(
                                              `items[${index}].amount`,
                                              amountReplace(e.target.value) ?? ''
                                            )
                                          }}
                                          onBlur={handleBlur}
                                          error={isError(`items[${index}].amount`)}
                                          helperText={isError(`items[${index}].amount`)
                                            ? getIn(
                                              errors,
                                              `items[${index}].amount`
                                            )
                                            : null
                                          }
                                          className={classes.fieldSize}
                                          InputProps={{ required: false }}
                                        />
                                      </Grid>
                                      <Grid
                                        item
                                        xl={6}
                                        xs={6}
                                        sm={6}
                                        className={classes.fieldSize}
                                      >
                                        <CurrencyPicker
                                          name={`items[${index}].currency`}
                                          label={t('currency.label')}
                                          selectedItemCode={
                                            values.items[index].currency ?? null
                                          }
                                          required={true}
                                          onValueChanged={(
                                            selectedCurrencyCode: string | null
                                          ) => {
                                            setFieldValue(
                                              `items[${index}].currency`,
                                              selectedCurrencyCode
                                            )
                                          }}
                                          onBlur={handleBlur}
                                          error={isError(`items[${index}].currency`)}
                                          helperText={isError(`items[${index}].currency`)
                                            ? getIn(
                                              errors,
                                              `items[${index}].currency`
                                            )
                                            : null
                                          }
                                        />
                                      </Grid>
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={12}
                                      xl={12}
                                      container
                                      className={classes.gridItem}
                                      spacing={2}
                                    >
                                      <Attachments
                                        name={`items[${index}].attachmentsPHOTO`}
                                        caseReportId={params.caseReportId}
                                        labelText={t(
                                          'uploadAttachments.ITEMPL.PHOTO'
                                        )}
                                        attachmentType={AttachmentType.PHOTO}
                                        part={PartType.ITEMPL}
                                        //itemOrderNum=undefined
                                        attachments={
                                          values.items[index]?.attachmentsPHOTO
                                        }
                                        onAttachmentsChanged={(
                                          event: AttachmentsChangedEvent
                                        ) => {
                                          setFieldValue(
                                            `items[${index}].attachmentsPHOTO`,
                                            event.value.attachments
                                          )
                                        }}
                                        onBlur={handleBlur}
                                        required
                                        error={isError(`items[${index}].attachmentsPHOTO`)}
                                        helperText={isError(`items[${index}].attachmentsPHOTO`)
                                          ? getIn(
                                            errors,
                                            `items[${index}].attachmentsPHOTO`
                                          )
                                          : null
                                        }
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xs={12}
                                      sm={12}
                                      xl={12}
                                      container
                                      className={classes.gridItem}
                                      spacing={2}
                                    >
                                      <Attachments
                                        name={`items[${index}].attachmentsINVOICE`}
                                        caseReportId={params.caseReportId}
                                        labelText={t(
                                          'uploadAttachments.ITEMPL.INVOICE'
                                        )}
                                        attachmentType={AttachmentType.INVOICE}
                                        part={PartType.ITEMPL}
                                        //itemOrderNum=undefined
                                        attachments={
                                          values.items[index].attachmentsINVOICE
                                        }
                                        onAttachmentsChanged={(
                                          event: AttachmentsChangedEvent
                                        ) => {
                                          setFieldValue(
                                            `items[${index}].attachmentsINVOICE`,
                                            event.value.attachments
                                          )
                                        }}
                                        onBlur={handleBlur}
                                        required={false}
                                        error={isError(`items[${index}].attachmentsINVOICE`)}
                                        helperText={isError(`items[${index}].attachmentsINVOICE`)
                                          ? getIn(
                                            errors,
                                            `items[${index}].attachmentsINVOICE`
                                          )
                                          : null
                                        }
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xl={12}
                                      xs={12}
                                      sm={12}
                                      className={classes.row}
                                    >
                                      <Grid
                                        item
                                        xl={6}
                                        xs={6}
                                        sm={6}
                                        className={classes.fieldSize}
                                      >
                                        <ButtonSimply
                                          onClick={() => {
                                            const initialValue = {
                                              ...formikValueForNewItem,
                                            }
                                            push(initialValue)
                                          }}
                                        >
                                          {t('liabilityForm.items.addItem')}
                                        </ButtonSimply>
                                      </Grid>
                                    </Grid>
                                  </React.Fragment>
                                )
                              })}
                          </React.Fragment>
                        )}
                      />
                    )}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      className={classes.gridItem}
                    />
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid item xl={12} xs={12} sm={12} className={classes.row}>
                        <BooleanField
                          aria-label="legalExpensesExists"
                          name="legalExpensesExists"
                          value={values.legalExpensesExists}
                          label={t('liabilityForm.legalExpensesExists.label')}
                          onValueChange={(value) => setFieldValue('legalExpensesExists', value)}
                          required
                          yesText={t('yes')}
                          noText={t('no')}
                          noFirst
                          error={isError('legalExpensesExists')}
                          helperText={isError('legalExpensesExists') ? errors.legalExpensesExists : null
                          }
                        />
                      </Grid>
                    </Grid>
                    {values.legalExpensesExists && (
                      <React.Fragment>
                        {values?.legalExpenses != null &&
                          values.legalExpenses.costs.map(
                            (item: any, index: any) => {
                              return (
                                <React.Fragment key={index}>
                                  <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    xl={12}
                                    container
                                    className={classes.gridItem}
                                    spacing={2}
                                  >
                                    <Grid
                                      item
                                      xl={6}
                                      xs={6}
                                      sm={6}
                                      className={classes.fieldSize}
                                    >
                                      <TextField
                                        name={`legalExpenses.costs[${index}].amount`}
                                        value={
                                          values.legalExpenses.costs[index].amount
                                        }
                                        label={t('liabilityForm.amount.label')}
                                        placeholder={t(
                                          'liabilityForm.amount.hint'
                                        )}
                                        regex={amountFilter}
                                        required
                                        inputProps={{ maxLength: 12 }}
                                        onChange={(e) => {
                                          setFieldValue(
                                            `legalExpenses.costs[${index}].amount`,
                                            amountReplace(e.target.value) ?? ''
                                          )
                                        }}
                                        onBlur={handleBlur}
                                        error={isError(`legalExpenses.costs[${index}].amount`)
                                        }
                                        helperText={isError(`legalExpenses.costs[${index}].amount`)
                                          ? getIn(
                                            errors,
                                            `legalExpenses.costs[${index}].amount`
                                          )
                                          : null
                                        }
                                        className={classes.fieldSize}
                                        InputProps={{ required: false }}
                                      />
                                    </Grid>
                                    <Grid
                                      item
                                      xl={6}
                                      xs={6}
                                      sm={6}
                                      className={classes.fieldSize}
                                    >
                                      <CurrencyPicker
                                        name={`legalExpenses.costs[${index}].currency`}
                                        label={t('currency.label')}
                                        selectedItemCode={
                                          values.legalExpenses.costs[index]
                                            .currency ?? null
                                        }
                                        onValueChanged={(
                                          selectedCurrencyCode: string | null
                                        ) => {
                                          setFieldValue(
                                            `legalExpenses.costs[${index}].currency`,
                                            selectedCurrencyCode
                                          )
                                        }}
                                        error={isError(`legalExpenses.costs[${index}].currency`)}
                                        helperText={isError(`legalExpenses.costs[${index}].currency`)
                                          ? getIn(
                                            errors,
                                            `legalExpenses.costs[${index}].currency`
                                          )
                                          : null
                                        }
                                        required={true}
                                        onBlur={handleBlur}
                                      />
                                    </Grid>
                                  </Grid>
                                  {/* ************Suppressed by ERGO request
                                <Grid
                                  item
                                  xs={12}
                                  sm={12}
                                  xl={12}
                                  container
                                  className={classes.gridItem}
                                  spacing={2}
                                >
                                  <Grid
                                    item
                                    xl={12}
                                    xs={12}
                                    sm={12}
                                    className={classes.row}
                                  >
                                    <FormControl component="fieldset">
                                      <LabelMessage
                                        name={`legalExpenses.costs.${index}.paidDirectly`}
                                        touched={touched}
                                        errors={errors}
                                        text={t('liabilityForm.paidDirectly.label')}
                                      />
                                      <RadioGroup
                                        aria-label={`legalExpenses.costs.[${index}].paidDirectly`}
                                        name={`legalExpenses.costs.${index}.paidDirectly`}
                                        value={
                                          values?.legalExpenses.costs[index]
                                            ?.paidDirectly
                                        }
                                        onChange={(e) => {
                                          setFieldValue(
                                            `legalExpenses.costs[${index}].paidDirectly`,
                                            e.target.value ?? ''
                                          )
                                        }}
                                        className={classes.row}
                                      >
                                        <FormControlLabel
                                          value="true"
                                          control={<Radio />}
                                          label={t(
                                            'liabilityForm.paidDirectly.true'
                                          )}
                                        />
                                        <FormControlLabel
                                          value="false"
                                          control={<Radio />}
                                          label={t(
                                            'liabilityForm.paidDirectly.false'
                                          )}
                                        />
                                      </RadioGroup>
                                      <ErrorMessage
                                        name={`legalExpenses.costs.${index}.paidDirectly`}
                                        component="div"
                                        className={classes.checkError}
                                      />
                                    </FormControl>
                                  </Grid>
                                </Grid>
                                          ********************* */}
                                  <Attachments
                                    name={`legalExpenses.costs[${index}].attachmentsINVOICE`}
                                    caseReportId={params.caseReportId}
                                    labelText={t(
                                      'uploadAttachments.COSTLE.INVOICE'
                                    )}
                                    attachmentType={AttachmentType.INVOICE}
                                    part={PartType.COSTLE}
                                    //itemOrderNum=undefined
                                    attachments={
                                      values.legalExpenses.costs[index]
                                        .attachmentsINVOICE
                                    }
                                    onAttachmentsChanged={(
                                      event: AttachmentsChangedEvent
                                    ) => {
                                      //console.log('onAttachmentsChanged: ', event)
                                      // aktualizace values formiku
                                      setFieldValue(
                                        `legalExpenses.costs[${index}].attachmentsINVOICE`,
                                        event.value.attachments
                                      )
                                    }}
                                    onBlur={handleBlur}
                                    required={true}
                                    error={isError(`legalExpenses.costs[${index}].attachmentsINVOICE`)}
                                    helperText={isError(`legalExpenses.costs[${index}].attachmentsINVOICE`)
                                      ? getIn(
                                        errors,
                                        `legalExpenses.costs[${index}].attachmentsINVOICE`
                                      )
                                      : null
                                    }
                                  />
                                </React.Fragment>
                              )
                            }
                          )}
                      </React.Fragment>
                    )}
                    <ErrorSubmitBack
                      formik={formikRef.current}
                      backLinkText={t('thirdPg.backBtn')}
                      onBackLinkClick={handleBackButtonClicked}
                    >
                      {t('continueBtn')}
                    </ErrorSubmitBack>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Ga4Form >
  )
}

export default LiabilityForm
